import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'

/**
 * O contrato de suporte por horas, e quanto do ciclo corrente já foi usado.
 *
 * O número vem pronto do banco de propósito: ele mistura o que a equipe lançou
 * em cada chamado, a estimativa em pontos de quem ainda não lançou, e o
 * fechamento manual da PM, que vence os dois. Refazer essa conta no cliente
 * seria o painel virando fonte primária de um número que ele não mediu.
 */

export type ConsumoDoCiclo = {
  horasPorCiclo: number | null
  horasUsadas: number
  /** ajuste, lancamento, estimativa, misto, vazio ou sem_contrato. */
  origem: string
  inicio: string | null
  fim: string | null
  /** orcamento, proximo_ciclo ou combinar. */
  aoEstourar: string | null
}

const SEM_CONTRATO: ConsumoDoCiclo = {
  horasPorCiclo: null,
  horasUsadas: 0,
  origem: 'sem_contrato',
  inicio: null,
  fim: null,
  aoEstourar: null,
}

export async function getConsumoDoCiclo(
  client: SupabaseClient,
  initiativeId: number,
): Promise<ConsumoDoCiclo> {
  const { data, error } = await client
    .schema('portal')
    .rpc('consumo_do_ciclo', { p_initiative_id: initiativeId })

  if (error) throw new SupabaseError(error.message, error)

  const linha = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null
  if (!linha) return SEM_CONTRATO

  return {
    horasPorCiclo: linha.horas_contratadas == null ? null : Number(linha.horas_contratadas),
    horasUsadas: Number(linha.horas_consumidas ?? 0),
    origem: String(linha.origem ?? 'sem_contrato'),
    inicio: (linha.inicio as string | null) ?? null,
    fim: (linha.fim as string | null) ?? null,
    aoEstourar: (linha.ao_estourar as string | null) ?? null,
  }
}

/**
 * O cartão do ciclo no portal: o ciclo (o atual, ou o pedido pelo seletor), os
 * pacotes e a lista de ciclos do contrato. Vem de portal.ciclo_de_suporte, que
 * devolve só os campos do corte do cliente (ADR-098, seção 4) e já soma a hora
 * sem pacote no último pacote. `null` quando não há contrato de suporte ativo.
 */
export type TipoDePacote = 'contrato' | 'adicional' | 'cortesia'

export type PacoteDoCiclo = {
  tipo: TipoDePacote
  ordem: number
  horas: number
  consumo: number
  restante: number
  excedente: number
  inicio: string
  fim: string | null
  status: 'aberto' | 'encerrado'
  vale_ate: string
  cheio_em: string | null
}

export type CicloDoContrato = {
  id: string
  numero: number
  inicio: string
  fim: string
  status: 'aberta' | 'fechada'
  consumo: number
  teto: number
  excedente: number
  fechada_em: string | null
}

export type CicloDeSuporte = {
  modelo: string
  franquia: number | null
  ciclo: CicloDoContrato & {
    atual: boolean
    terminou: boolean
    dias_corridos: number
    dias_no_ciclo: number
    pct_corrido: number
    restante: number
    projecao: number
    projecao_excedente: number
    horas_estimadas: number
    itens: number
  }
  pacotes: PacoteDoCiclo[]
  ciclos: CicloDoContrato[]
}

export async function getCicloDeSuporte(
  client: SupabaseClient,
  initiativeId: number,
  competenciaId: string | null = null,
): Promise<CicloDeSuporte | null> {
  // biome-ignore lint/suspicious/noExplicitAny: RPC nascida depois da última regeração dos types
  const { data, error } = await (client.schema('portal') as any).rpc('ciclo_de_suporte', {
    p_initiative_id: initiativeId,
    p_competencia_id: competenciaId,
  })
  if (error) throw new SupabaseError(error.message, error)
  return (data as CicloDeSuporte | null) ?? null
}

/** Em que foram as horas de um ciclo: um item por tarefa ou hora avulsa. */
export type ItemDoCiclo = {
  numero: number | null
  titulo: string
  aberto_por: string | null
  aberto_por_nome: string | null
  aberto_pela_equipe: boolean
  especie: 'tarefa' | 'avulso'
  horas: number
  estimada: boolean
  quando: string | null
  pacote_tipo: TipoDePacote | null
}

export async function listItensDoCiclo(
  client: SupabaseClient,
  initiativeId: number,
  competenciaId: string,
): Promise<ItemDoCiclo[]> {
  // biome-ignore lint/suspicious/noExplicitAny: RPC nascida depois da última regeração dos types
  const { data, error } = await (client.schema('portal') as any).rpc('itens_do_ciclo', {
    p_initiative_id: initiativeId,
    p_competencia_id: competenciaId,
  })
  if (error) throw new SupabaseError(error.message, error)
  return ((data ?? []) as ItemDoCiclo[]).map((i) => ({ ...i, horas: Number(i.horas) }))
}
